import React from 'react';
import { Link } from 'react-router-dom';
import {
  Check, Hammer, Sparkles, Lock, ArrowLeft, Flame, Heart, Cloud, MessageCircle,
  Activity, CreditCard, Link2, Shield, Users, Utensils, Image as ImageIcon,
  Globe, Trophy, Mountain, Smartphone, MapPin, ShieldCheck,
} from 'lucide-react';
import { ROADMAP_ITEMS, SECTION_META } from '../data/roadmap';
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from '../components/ui/accordion';

// Map icon string → lucide component so the data file stays JSON-friendly.
const ICON_MAP = {
  Hammer, Sparkles, Lock, Flame, Heart, Cloud, MessageCircle, Activity,
  CreditCard, Link2, Shield, Users, Utensils, Image: ImageIcon, Globe,
  Trophy, Mountain, Smartphone, MapPin, ShieldCheck, Check,
};

const STATUS_ICON = { shipped: Check, soon: Sparkles };

const Roadmap = () => {
  // Group items by status
  const grouped = ROADMAP_ITEMS.reduce((acc, it) => {
    (acc[it.status] = acc[it.status] || []).push(it);
    return acc;
  }, {});

  return (
    <div className="bg-transparent text-enf-text-primary min-h-screen pt-20 pb-16">
      <div className="max-w-4xl mx-auto px-6">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-enf-text-muted hover:text-enf-role-info transition-colors mb-6">
          <ArrowLeft className="w-4 h-4" /> Back to Enferno
        </Link>

        <div className="mb-10">
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-enf-role-info font-bold mb-3">
            <Flame className="w-3.5 h-3.5 scale-x-[-1]" /> Private Beta · v0.5
          </div>
          <h1 className="font-display text-5xl md:text-7xl tracking-wide leading-none mb-4">The road ahead.</h1>
          <p className="text-enf-text-secondary text-base sm:text-lg max-w-2xl leading-relaxed">
            Enferno is being built in the open by IronFire Technologies. You're seeing it because you matter to its
            outcome — family, board, partners, prospective donors. Here's what's in your hands today and where
            we're headed next.
          </p>
        </div>

        <Accordion type="multiple" defaultValue={['shipped', 'soon']} className="space-y-3">
          {['shipped', 'soon'].map((status) => {
            const items = grouped[status] || [];
            if (items.length === 0) return null;
            const meta = SECTION_META[status];
            const SectionIcon = STATUS_ICON[status];
            return (
              <AccordionItem
                key={status}
                value={status}
                className="border border-enf-surface-border rounded-xl bg-enf-surface-deep/40 overflow-hidden"
                data-testid={`roadmap-section-${status}`}
              >
                <AccordionTrigger
                  className="px-5 py-4 hover:no-underline hover:bg-enf-surface-mid/40 transition-colors"
                  data-testid={`roadmap-section-${status}-trigger`}
                >
                  <div className="flex items-center gap-3 flex-1">
                    <div className={`w-9 h-9 rounded-md flex items-center justify-center flex-shrink-0 ${
                      meta.color === 'enf-accent' ? 'bg-enf-accent/15 border border-enf-accent-fg/40' :
                      meta.color === 'enf-role-info' ? 'bg-enf-role-info/15 border border-enf-role-info/40' :
                      'bg-enf-border-special/15 border border-enf-border-special/40'
                    }`}>
                      <SectionIcon className={`w-4 h-4 ${
                        meta.color === 'enf-accent' ? 'text-enf-accent-fg' :
                        meta.color === 'enf-role-info' ? 'text-enf-role-info' :
                        'text-enf-border-special'
                      }`} />
                    </div>
                    <div className="text-left">
                      <div className={`text-[10px] uppercase tracking-[0.3em] font-bold ${
                        meta.color === 'enf-accent' ? 'text-enf-accent-fg' :
                        meta.color === 'enf-role-info' ? 'text-enf-role-info' :
                        'text-enf-border-special'
                      }`}>{meta.label}</div>
                      <div className="text-enf-text-muted text-xs">{items.length} item{items.length !== 1 ? 's' : ''}</div>
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-5">
                  <div className="grid sm:grid-cols-2 gap-3 pt-2">
                    {items.map((it, i) => {
                      const ItIcon = ICON_MAP[it.icon] || Hammer;
                      return (
                        <div key={i} className="p-4 bg-enf-surface-deep border border-enf-surface-border rounded-xl transition-colors hover:border-enf-surface-border">
                          <div className="flex items-start gap-3">
                            <div className={`w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0 ${
                              status === 'shipped' ? 'bg-enf-accent/10 text-enf-accent-fg' :
                              'bg-enf-border-special/10 text-enf-border-special'
                            }`}>
                              <ItIcon className="w-4 h-4" />
                            </div>
                            <div className="flex-1">
                              <div className="font-semibold text-sm text-enf-text-primary mb-1">{it.title}</div>
                              <div className="text-xs text-enf-text-muted leading-relaxed">{it.detail}</div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>

        <div className="mt-12 p-6 bg-enf-surface-deep border border-enf-border-special/30 rounded-xl">
          <div className="text-xs uppercase tracking-[0.3em] text-enf-role-info font-bold mb-2">Help shape it</div>
          <h3 className="font-display text-3xl tracking-wide leading-none mb-3">Your reactions matter most.</h3>
          <p className="text-enf-text-secondary text-sm leading-relaxed mb-4">
            What's missing? What's confusing? What would make you genuinely use this every day? Tap the
            Feedback button at the bottom-left of any screen — it goes straight to my inbox.
          </p>
          <Link to="/progress" className="inline-flex items-center gap-2 px-5 py-2.5 bg-enf-accent hover:bg-enf-accent-hover rounded-md text-sm font-semibold transition-colors">
            Back to my progress
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Roadmap;
